import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MainLayout } from '../components/Layout';
import { 
  Package, 
  Layers, 
  FileSpreadsheet, 
  ClipboardCheck, 
  Clock, 
  CheckCircle, 
  XCircle,
  TrendingUp,
  ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MetricCard = ({ title, value, icon: Icon, trend, color = 'zinc', onClick }) => {
  const colorStyles = {
    zinc: 'border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700',
    red: 'border-[#C3002F]/30 hover:border-[#C3002F]/50',
    amber: 'border-amber-500/30 hover:border-amber-500/50',
    emerald: 'border-emerald-500/30 hover:border-emerald-500/50',
    cyan: 'border-cyan-500/30 hover:border-cyan-500/50'
  };

  const iconColors = {
    zinc: 'text-zinc-500 dark:text-zinc-400',
    red: 'text-[#C3002F]',
    amber: 'text-amber-500 dark:text-amber-400',
    emerald: 'text-emerald-500 dark:text-emerald-400',
    cyan: 'text-cyan-500 dark:text-cyan-400'
  };

  return (
    <div 
      onClick={onClick}
      className={`metric-card p-6 bg-white dark:bg-zinc-900 border ${colorStyles[color]} rounded-lg cursor-pointer group transition-colors`}
      data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-zinc-500 font-mono mb-1">{title}</p>
          <p className="text-3xl font-bold text-zinc-900 dark:text-white tracking-tight font-mono">{value}</p>
          {trend && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              {trend}
            </p>
          )}
        </div>
        <div className={`p-3 bg-zinc-100 dark:bg-zinc-800/50 rounded ${iconColors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="mt-4 flex items-center text-xs text-zinc-500 group-hover:text-zinc-700 dark:group-hover:text-zinc-400 transition-colors">
        View details <ArrowRight className="w-3 h-3 ml-1" />
      </div>
    </div>
  );
};

const RecentActivityItem = ({ card }) => {
  const getStatusStyle = (status) => {
    switch (status) {
      case 'approved': return 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400';
      case 'rejected': return 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400';
      case 'pending': return 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400';
      default: return 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-400';
    }
  };

  return (
    <tr className="border-b border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900/50 transition-colors">
      <td className="py-3 px-4 font-mono text-sm text-zinc-900 dark:text-zinc-100">{card.part_number}</td>
      <td className="py-3 px-4 text-zinc-700 dark:text-zinc-300">{card.part_name}</td>
      <td className="py-3 px-4 text-zinc-500 dark:text-zinc-400">{card.family_name}</td>
      <td className="py-3 px-4 font-mono text-emerald-600 dark:text-emerald-400">₹{(card.dbp || card.dealer_price || 0).toLocaleString()}</td>
      <td className="py-3 px-4">
        <span className={`px-2 py-1 text-xs rounded ${getStatusStyle(card.status)}`}>
          {card.status}
        </span>
      </td>
      <td className="py-3 px-4 text-zinc-500 text-sm">{card.created_by_name}</td>
    </tr>
  );
};

export default function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [recentCards, setRecentCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, cardsRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`),
        axios.get(`${API}/cost-cards`)
      ]);
      setSummary(summaryRes.data);
      setRecentCards(cardsRes.data.slice(0, 10));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin w-8 h-8 border-2 border-[#C3002F] border-t-transparent rounded-full" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-8 space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-zinc-900 dark:text-white tracking-tight">Dashboard</h1>
            <p className="text-zinc-500 dark:text-zinc-400 mt-1">Welcome back, {user?.name}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Potential Revenue</p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 font-mono">
              ₹{(summary?.total_potential_revenue || 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Parts"
            value={summary?.total_parts || 0}
            icon={Package}
            color="zinc"
            onClick={() => navigate('/parts')}
          />
          <MetricCard
            title="Part Families"
            value={summary?.total_families || 0}
            icon={Layers}
            color="cyan"
            onClick={() => navigate('/templates')}
          />
          <MetricCard
            title="Pending Approvals"
            value={summary?.pending_approvals || 0}
            icon={Clock}
            color="amber"
            onClick={() => navigate('/cost-cards?status=pending')}
          />
          <MetricCard
            title="Approved Cards"
            value={summary?.approved_cards || 0}
            icon={CheckCircle}
            color="emerald"
            onClick={() => navigate('/cost-cards?status=approved')}
          />
        </div>

        {/* Secondary metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            title="Cost Templates"
            value={summary?.total_templates || 0}
            icon={FileSpreadsheet}
            color="zinc"
            onClick={() => navigate('/templates')}
          />
          <MetricCard
            title="Rejected Cards"
            value={summary?.rejected_cards || 0}
            icon={XCircle}
            color="red"
            onClick={() => navigate('/cost-cards?status=rejected')}
          />
          <MetricCard
            title="Avg Dealer Markup"
            value={`${summary?.avg_dealer_markup_pct || 15}%`}
            icon={TrendingUp}
            trend="+2.3% vs last month"
            color="emerald"
            onClick={() => navigate('/analytics')}
          />
        </div>

        {/* Recent Activity Table */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Recent Cost Cards</h2>
            <button 
              onClick={() => navigate('/cost-cards')}
              className="text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white flex items-center gap-1 transition-colors"
            >
              View all <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="recent-cards-table">
              <thead>
                <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
                  <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Part Number</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Part Name</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Family</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Dealer Price</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Created By</th>
                </tr>
              </thead>
              <tbody>
                {recentCards.length > 0 ? (
                  recentCards.map((card) => (
                    <RecentActivityItem key={card.id} card={card} />
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-zinc-500">
                      No cost cards created yet. Start by searching for a part.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
